import { LitElement, html, css, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'

import type { Plan } from '../../schemas/plan.js'
import type { FieldLogEntry, FieldLogEntryType } from '../../schemas/field-log-entry.js'
import type { JSONContent } from '../../lib/tiptap-config.js'
import { fieldLogEntryStorage } from '../../storage/field-log-entry-storage.js'
import { syncChildrenOnNavigate } from '../../storage/sync-manager.js'
import { blockStore } from '../../services/moderation/block-store.js'
import { showToast } from '../common/cairn-toast.js'
void import('../ui/rich-text-editor.js')
void import('../ui/rich-text-renderer.js')
import './decision-framework-view.js'

const TYPE_ICONS: Record<FieldLogEntryType, string> = {
  comment: 'chat_bubble',
  log: 'edit_note',
  report: 'summarize',
}

const TYPE_LABELS: Record<FieldLogEntryType, string> = {
  comment: 'Comment',
  log: 'Log',
  report: 'Report',
}

interface Member {
  userId: string
  displayName: string
  picture?: string
  role: string
}

@customElement('field-log-tab')
export class FieldLogTab extends LitElement {
  @property({ type: String }) planId = ''
  @property({ type: Object }) plan: Plan | null = null
  @property({ type: String }) currentUserId = ''
  @property({ type: String }) currentUserRole = 'viewer'
  @property({ type: Array }) members: Member[] = []
  @property({ type: Number }) lastSyncTime = 0

  @state() private entries: FieldLogEntry[] = []
  @state() private filter: 'all' | FieldLogEntryType = 'all'
  @state() private showForm = false
  @state() private editingEntry: FieldLogEntry | null = null
  @state() private formType: FieldLogEntryType = 'comment'
  @state() private formContent: JSONContent | undefined = undefined
  @state() private showBlockedEntries = false
  @state() private loading = true
  @state() private sortAscending = false
  @state() private expandedTimeId: string | null = null

  static override styles = css`
    :host {
      display: block;
    }

    .filter-bar {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px 16px;
      overflow-x: auto;
    }

    .filter-chip {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 6px 12px;
      border-radius: var(--cairn-border-radius-xl, 16px);
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      background: transparent;
      color: var(--cairn-text-color, #1e293b);
      font-size: var(--cairn-text-size-sm, 14px);
      font-family: inherit;
      cursor: pointer;
      white-space: nowrap;
      transition: background var(--cairn-animation-duration, 0.2s);
    }

    .filter-chip:hover {
      background: var(--cairn-background-color-alt, #f1f5f9);
    }

    .filter-chip[data-active] {
      background: var(--cairn-primary-color, #1976d2);
      border-color: var(--cairn-primary-color, #1976d2);
      color: #fff;
    }

    .filter-chip .material-icons {
      font-size: 16px;
    }

    .add-btn {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 6px 16px;
      border-radius: var(--cairn-border-radius-xl, 16px);
      border: none;
      background: var(--cairn-primary-color, #1976d2);
      color: #fff;
      font-size: var(--cairn-text-size-sm, 14px);
      font-family: inherit;
      cursor: pointer;
      white-space: nowrap;
    }

    .add-btn .material-icons {
      font-size: 18px;
    }

    .form-card {
      margin: 0 16px 16px;
      padding: 16px;
      border-radius: var(--cairn-border-radius-lg, 12px);
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      background: var(--cairn-surface-color, #ffffff);
    }

    .type-selector {
      display: flex;
      gap: 8px;
      margin-bottom: 12px;
    }

    .type-chip {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 6px 12px;
      border-radius: var(--cairn-border-radius-xl, 16px);
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      background: transparent;
      color: var(--cairn-text-color, #1e293b);
      font-size: var(--cairn-text-size-sm, 14px);
      font-family: inherit;
      cursor: pointer;
    }

    .type-chip[data-active] {
      background: var(--cairn-primary-color, #1976d2);
      color: #fff;
      border-color: var(--cairn-primary-color, #1976d2);
    }

    .type-chip .material-icons {
      font-size: 16px;
    }

    .form-actions {
      display: flex;
      justify-content: flex-end;
      gap: 8px;
      margin-top: 12px;
    }

    .form-actions button {
      padding: 8px 20px;
      border-radius: var(--cairn-border-radius-full, 9999px);
      border: none;
      font-size: var(--cairn-text-size-sm, 14px);
      font-family: inherit;
      cursor: pointer;
    }

    .btn-cancel {
      background: transparent;
      color: var(--cairn-text-color, #1e293b);
    }

    .btn-save {
      background: var(--cairn-primary-color, #1976d2);
      color: #fff;
    }

    .btn-save:disabled {
      opacity: 0.5;
      cursor: default;
    }

    .entry-list {
      padding: 0 16px 16px;
    }

    .entry {
      display: flex;
      gap: 12px;
      padding: 14px 0;
    }

    .entry-icon {
      font-size: 20px;
      color: var(--cairn-primary-color, #1976d2);
      flex-shrink: 0;
      width: 24px;
      text-align: center;
      padding-top: 2px;
    }

    .entry-body {
      flex: 1;
      min-width: 0;
    }

    .entry-header {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 4px;
    }

    .entry-author {
      font-size: var(--cairn-text-size-sm, 14px);
      font-weight: var(--cairn-font-weight-medium, 500);
      color: var(--cairn-text-color, #1e293b);
    }

    .entry-time {
      font-size: var(--cairn-text-size-xs, 12px);
      color: var(--cairn-text-secondary-color, #64748b);
      cursor: pointer;
      text-decoration: underline;
      text-decoration-style: dotted;
      text-underline-offset: 2px;
    }

    .entry-flag {
      display: inline-flex;
      align-items: center;
      gap: 2px;
      font-size: var(--cairn-text-size-xs, 12px);
      color: var(--cairn-error-color, #dc2626);
    }

    .entry-flag .material-icons {
      font-size: 14px;
    }

    .entry-content {
      font-size: var(--cairn-text-size-sm, 14px);
      line-height: var(--cairn-line-height, 1.5);
    }

    .entry-actions {
      display: flex;
      align-items: center;
      gap: 4px;
      margin-left: auto;
    }

    .action-btn {
      display: inline-flex;
      align-items: center;
      padding: 4px;
      border: none;
      background: transparent;
      color: var(--cairn-text-secondary-color, #64748b);
      cursor: pointer;
      border-radius: var(--cairn-border-radius-sm, 4px);
    }

    .action-btn:hover {
      background: var(--cairn-background-color-alt, #f1f5f9);
    }

    .action-btn .material-icons {
      font-size: 18px;
    }

    .date-separator {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 8px 0;
      font-size: var(--cairn-text-size-xs, 12px);
      font-weight: var(--cairn-font-weight-medium, 500);
      color: var(--cairn-text-secondary-color, #64748b);
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    .date-separator::before,
    .date-separator::after {
      content: '';
      flex: 1;
      border-top: 1px solid var(--cairn-border-color, #e2e8f0);
    }

    .filter-bar-actions {
      margin-left: auto;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .toolbar-btn {
      display: inline-flex;
      align-items: center;
      padding: 6px;
      border: none;
      background: transparent;
      color: var(--cairn-text-secondary-color, #64748b);
      cursor: pointer;
      border-radius: var(--cairn-border-radius-sm, 4px);
    }

    .toolbar-btn:hover {
      background: var(--cairn-background-color-alt, #f1f5f9);
    }

    .toolbar-btn .material-icons {
      font-size: 20px;
    }

    .empty-state {
      color: var(--cairn-text-secondary-color, #64748b);
      font-style: italic;
      padding: 24px 0;
      text-align: center;
    }

    .loading {
      text-align: center;
      padding: 24px;
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .blocked-banner {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 16px;
      margin: 0 16px 8px;
      border-radius: var(--cairn-border-radius, 8px);
      background: var(--cairn-background-color-alt, #f1f5f9);
      font-size: var(--cairn-text-size-sm, 14px);
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .blocked-banner button {
      margin-left: auto;
      padding: 4px 12px;
      border: none;
      border-radius: var(--cairn-border-radius-lg, 12px);
      background: transparent;
      color: var(--cairn-primary-color, #1976d2);
      font-size: var(--cairn-text-size-sm, 14px);
      font-family: inherit;
      cursor: pointer;
    }

    .entry-blocked {
      opacity: 0.5;
    }

    .material-icons {
      font-family: 'Material Symbols Outlined';
      font-weight: normal;
      font-style: normal;
      font-size: 24px;
      line-height: 1;
      letter-spacing: normal;
      text-transform: none;
      display: inline-block;
      white-space: nowrap;
      word-wrap: normal;
      direction: ltr;
      -webkit-font-feature-settings: 'liga';
      -webkit-font-smoothing: antialiased;
    }
  `

  override connectedCallback(): void {
    super.connectedCallback()
    void this.loadEntries()
  }

  override updated(changed: Map<string, unknown>): void {
    if (changed.has('planId') && this.planId) {
      void this.loadEntries()
    }
    if (changed.has('lastSyncTime') && this.lastSyncTime > 0) {
      void this.loadEntries()
    }
  }

  private async loadEntries(): Promise<void> {
    if (!this.planId) return
    this.loading = true
    try {
      this.entries = await fieldLogEntryStorage.getEntriesForPlan(this.planId)
    } catch {
      this.entries = []
    }
    this.loading = false
  }

  private get canWrite(): boolean {
    return this.currentUserRole === 'member' || this.currentUserRole === 'leader'
  }

  private get isLeader(): boolean {
    return this.currentUserRole === 'leader'
  }

  private get filteredEntries(): { visible: FieldLogEntry[]; blocked: FieldLogEntry[] } {
    let entries = this.sortAscending ? [...this.entries].reverse() : this.entries
    if (this.filter !== 'all') {
      entries = entries.filter(e => e.type === this.filter)
    }
    const visible: FieldLogEntry[] = []
    const blocked: FieldLogEntry[] = []
    for (const e of entries) {
      if (blockStore.isBlocked(e.createdBy)) {
        blocked.push(e)
      } else {
        visible.push(e)
      }
    }
    return { visible, blocked }
  }

  private getMemberName(userId: string): string {
    const m = this.members.find(m => m.userId === userId)
    return m?.displayName ?? 'Unknown'
  }

  private formatTime(iso: string): string {
    const d = new Date(iso)
    const now = new Date()
    const diffMs = now.getTime() - d.getTime()
    const diffMin = Math.floor(diffMs / 60000)

    if (diffMin < 1) return 'just now'
    if (diffMin < 60) return `${diffMin}m ago`
    const diffHr = Math.floor(diffMin / 60)
    if (diffHr < 24) return `${diffHr}h ago`
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  }

  private formatDate(iso: string): string {
    const d = new Date(iso)
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const entryDate = new Date(d.getFullYear(), d.getMonth(), d.getDate())
    const diffDays = Math.round((today.getTime() - entryDate.getTime()) / 86400000)

    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Yesterday'
    return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
  }

  private formatFullTime(iso: string): string {
    const d = new Date(iso)
    return d.toLocaleString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  private toggleTime(entryId: string): void {
    this.expandedTimeId = this.expandedTimeId === entryId ? null : entryId
  }

  private async refreshEntries(): Promise<void> {
    if (this.planId) {
      await syncChildrenOnNavigate('plan', this.planId)
    }
    await this.loadEntries()
  }

  private openForm(type?: FieldLogEntryType): void {
    this.showForm = true
    this.editingEntry = null
    this.formType = type ?? 'comment'
    this.formContent = undefined
  }

  private editEntry(entry: FieldLogEntry): void {
    this.showForm = true
    this.editingEntry = entry
    this.formType = entry.type
    this.formContent = entry.content as JSONContent
  }

  private closeForm(): void {
    this.showForm = false
    this.editingEntry = null
    this.formContent = undefined
  }

  private handleContentChanged(e: CustomEvent<{ json: JSONContent; text: string }>): void {
    this.formContent = e.detail.json
  }

  private async saveEntry(): Promise<void> {
    if (!this.formContent) return

    if (this.editingEntry) {
      await fieldLogEntryStorage.updateEntry(this.editingEntry.id, {
        type: this.formType,
        content: this.formContent,
      })
    } else {
      await fieldLogEntryStorage.createEntry({
        planId: this.planId,
        type: this.formType,
        content: this.formContent,
        createdBy: this.currentUserId,
      })
    }

    this.closeForm()
    await this.loadEntries()
  }

  private async handleDelete(entry: FieldLogEntry): Promise<void> {
    await fieldLogEntryStorage.deleteEntry(entry.id)
    await this.loadEntries()
  }

  private async handleFlag(entry: FieldLogEntry): Promise<void> {
    await fieldLogEntryStorage.flagEntry(entry.id, this.currentUserId)
    showToast('info', 'Entry flagged — leaders have been notified')
    await this.loadEntries()
  }

  override render() {
    return html`
      <decision-framework-view .plan=${this.plan}></decision-framework-view>

      ${this.renderFilterBar()} ${this.showForm ? this.renderForm() : nothing}
      ${this.renderEntries()}
    `
  }

  private renderFilterBar() {
    const types: ('all' | FieldLogEntryType)[] = ['all', 'comment', 'log', 'report']
    return html`
      <div class="filter-bar">
        ${types.map(
          t => html`
            <button
              class="filter-chip"
              ?data-active=${this.filter === t}
              @click=${() => (this.filter = t)}
            >
              ${t === 'all'
                ? 'All'
                : html`<span class="material-icons">${TYPE_ICONS[t]}</span>${TYPE_LABELS[t]}`}
            </button>
          `
        )}
        <div class="filter-bar-actions">
          <button
            class="toolbar-btn"
            @click=${() => (this.sortAscending = !this.sortAscending)}
            title=${this.sortAscending ? 'Newest first' : 'Oldest first'}
          >
            <span class="material-icons"
              >${this.sortAscending ? 'arrow_upward' : 'arrow_downward'}</span
            >
          </button>
          <button class="toolbar-btn" @click=${this.refreshEntries} title="Refresh">
            <span class="material-icons">refresh</span>
          </button>
          ${this.canWrite
            ? html`
                <button class="add-btn" @click=${() => this.openForm()}>
                  <span class="material-icons">add</span>
                  Add
                </button>
              `
            : nothing}
        </div>
      </div>
    `
  }

  private renderForm() {
    const types: FieldLogEntryType[] = ['comment', 'log', 'report']
    return html`
      <div class="form-card">
        <div class="type-selector">
          ${types.map(
            t => html`
              <button
                class="type-chip"
                ?data-active=${this.formType === t}
                @click=${() => (this.formType = t)}
              >
                <span class="material-icons">${TYPE_ICONS[t]}</span>
                ${TYPE_LABELS[t]}
              </button>
            `
          )}
        </div>
        <rich-text-editor
          .value=${this.formContent}
          placeholder="Write your entry..."
          minHeight="80px"
          @content-changed=${this.handleContentChanged}
        ></rich-text-editor>
        <div class="form-actions">
          <button class="btn-cancel" @click=${this.closeForm}>Cancel</button>
          <button class="btn-save" ?disabled=${!this.formContent} @click=${this.saveEntry}>
            ${this.editingEntry ? 'Update' : 'Save'}
          </button>
        </div>
      </div>
    `
  }

  private renderEntries() {
    if (this.loading) {
      return html`<div class="loading">Loading entries...</div>`
    }

    const { visible, blocked } = this.filteredEntries

    if (visible.length === 0 && blocked.length === 0) {
      return html`<div class="empty-state">No field log entries yet</div>`
    }

    return html`
      ${blocked.length > 0
        ? html`
            <div class="blocked-banner">
              <span
                >${blocked.length} ${blocked.length === 1 ? 'entry' : 'entries'} from blocked users
                hidden</span
              >
              <button @click=${() => (this.showBlockedEntries = !this.showBlockedEntries)}>
                ${this.showBlockedEntries ? 'Hide' : 'Show'}
              </button>
            </div>
          `
        : nothing}
      <div class="entry-list">
        ${this.renderDateGroupedEntries(visible, false)}
        ${this.showBlockedEntries ? this.renderDateGroupedEntries(blocked, true) : nothing}
      </div>
    `
  }

  private renderDateGroupedEntries(entries: FieldLogEntry[], isBlocked: boolean) {
    const result: unknown[] = []
    let lastDate = ''
    for (const entry of entries) {
      const date = this.formatDate(entry.createdAt)
      if (date !== lastDate) {
        result.push(html`<div class="date-separator"><span>${date}</span></div>`)
        lastDate = date
      }
      result.push(this.renderEntry(entry, isBlocked))
    }
    return result
  }

  private renderEntry(entry: FieldLogEntry, isBlocked: boolean) {
    const isOwn = entry.createdBy === this.currentUserId
    const isFlagged = entry.flaggedBy.length > 0

    return html`
      <div class="entry ${isBlocked ? 'entry-blocked' : ''}">
        <span class="material-icons entry-icon">${TYPE_ICONS[entry.type] ?? 'note'}</span>
        <div class="entry-body">
          <div class="entry-header">
            <span class="entry-author">${this.getMemberName(entry.createdBy)}</span>
            <span class="entry-time" @click=${() => this.toggleTime(entry.id)}
              >${this.expandedTimeId === entry.id
                ? this.formatFullTime(entry.createdAt)
                : this.formatTime(entry.createdAt)}</span
            >
            <div class="entry-actions">
              ${isFlagged
                ? html`<span class="entry-flag"
                    ><span class="material-icons">flag</span>${entry.flaggedBy.length}</span
                  >`
                : nothing}
              ${this.canWrite
                ? html`
                    ${isOwn
                      ? html`
                          <button
                            class="action-btn"
                            @click=${() => this.editEntry(entry)}
                            title="Edit"
                          >
                            <span class="material-icons">edit</span>
                          </button>
                          <button
                            class="action-btn"
                            @click=${() => this.handleDelete(entry)}
                            title="Delete"
                          >
                            <span class="material-icons">delete</span>
                          </button>
                        `
                      : nothing}
                    <button class="action-btn" @click=${() => this.handleFlag(entry)} title="Flag">
                      <span class="material-icons">flag</span>
                    </button>
                    ${!isOwn && this.isLeader && isFlagged
                      ? html`
                          <button
                            class="action-btn"
                            @click=${() => this.handleDelete(entry)}
                            title="Delete flagged entry"
                          >
                            <span class="material-icons">delete</span>
                          </button>
                        `
                      : nothing}
                  `
                : nothing}
            </div>
          </div>
          <div class="entry-content">
            <rich-text-renderer .content=${entry.content}></rich-text-renderer>
          </div>
        </div>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'field-log-tab': FieldLogTab
  }
}
